import {
  getIsPdfLocationAnchor,
  isParagraphElement,
  parseMarkdownLocationLink,
} from '@aline/common';
import { Editor } from 'slate';
import type { SelectedReference } from 'src/editor/toolbars/right-panel/ai/extraction/reports/ExtractionJobDetail';

export type SelectedReferenceWithDocumentId = SelectedReference & {
  documentId: string;
  versionId: string;
};

/**
 * Parses a file location from the location string in the format:
 * #doc/<documentId>/ver/<versionId>/loc/<pageNum>:<rect>
 */
export function parseFileLocation(
  fileLocation: string,
  referenceText: string
): SelectedReferenceWithDocumentId | null {
  const parts = parseMarkdownLocationLink(fileLocation);
  if (!parts) return null;

  const { documentId, locationId, versionId } = parts;
  const selectedReference = parseFileLocationId(locationId, referenceText);
  if (!selectedReference) return null;

  return {
    ...selectedReference,
    documentId,
    versionId,
  };
}

/**
 * Parses a file location ID from the location string in the format:
 * #doc/<documentId>/ver/<versionId>/loc/<pageNum>:<rect>
 */
export function parseFileLocationId(
  fileLocationId: string,
  referenceText: string
): SelectedReference | null {
  const locationParts = fileLocationId.split(':');
  if (locationParts.length !== 5) {
    console.error('Invalid PDF location format:', fileLocationId);
    return null;
  }

  const [pageNumStr, ...rectStrings] = locationParts;
  const pageNum = parseInt(pageNumStr);
  const rect = rectStrings.map(parseFloat);
  if (Number.isNaN(pageNum) || rect.some(Number.isNaN)) {
    console.error('Invalid number format in location:', fileLocationId);
    return null;
  }

  return {
    __typename: 'FileLocation',
    pageNum,
    rect,
    referenceText,
  };
}

/**
 * Parses an Aline location from the location string in the format:
 * #doc/<documentId>/ver/<versionId>/loc/<locationId>
 */
export function parseAlineLocation(
  alineLocation: string,
  editor: Editor,
  referencedText: string
): SelectedReferenceWithDocumentId | null {
  const parts = parseMarkdownLocationLink(alineLocation);
  if (!parts) return null;

  const { documentId, locationId, versionId } = parts;
  const selectedReference = parseAlineLocationId(locationId, editor, referencedText);
  if (!selectedReference) return null;

  return {
    ...selectedReference,
    documentId,
    versionId,
  };
}

/**
 * Parses the Aline location anchor from the location string in the format:
 * #doc/<documentId>/ver/<versionId>/loc/<locationAnchor>
 */
export function parseAlineLocationId(
  alineLocationId: string,
  editor: Editor,
  referencedText: string
): SelectedReference | null {
  const entries = Editor.nodes(editor, {
    at: [],
    match: n => isParagraphElement(n) && n.locationId === alineLocationId,
    mode: 'all',
    voids: true,
  });
  const firstMatchingNodeEntry = entries.next().value;
  if (!firstMatchingNodeEntry) return null;

  return {
    __typename: 'AlineLocation',
    locationPath: firstMatchingNodeEntry[1],
    referenceText: referencedText,
  };
}

/**
 * Parses a location string in the format:
 * #doc/<documentId>/ver/<versionId>/loc/<locationId>
 */
export function parseLocation(
  locationString: string,
  referencedText: string,
  editor: Editor | null
): SelectedReferenceWithDocumentId | null {
  const parts = parseMarkdownLocationLink(locationString);
  if (!parts) return null;

  const { locationId } = parts;
  // PDF file locations are coordinate anchors (pageNum:rect); Aline ids are not.
  if (getIsPdfLocationAnchor(locationId)) return parseFileLocation(locationString, referencedText);
  if (!editor) return null;

  return parseAlineLocation(locationString, editor, referencedText);
}
